import { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ShapeXLogo from '../components/ShapeXLogo';
import PWAInstall from '../components/PWAInstall';
import { Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';

interface SignInProps {
  onSuccess: () => void;
  onSwitchToSignUp: () => void;
}

export default function SignIn({ onSuccess, onSwitchToSignUp }: SignInProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Preencha todos os campos para continuar.');
      return;
    }

    setLoading(true);
    const { error: authError } = await signIn(email, password);
    setLoading(false);

    if (authError) {
      if (authError.message.includes('Invalid login credentials')) {
        setError('E-mail ou senha incorretos.');
      } else if (authError.message.includes('Email not confirmed')) {
        setError('E-mail não verificado. Verifique sua caixa de entrada.');
      } else {
        setError('Erro ao entrar. Tente novamente.');
      }
    } else {
      onSuccess();
    }
  };

  // Estilos padronizados do Design System (Dashboard/Onboarding)
  const INPUT_BG = '#0D0D0D';
  const BORDER_COLOR = 'rgba(255, 255, 255, 0.1)';
  const ACCENT_RED = '#E53935';

  const inputClasses = "w-full rounded-xl border px-4 py-3.5 text-sm text-white focus:outline-none focus:border-[#E53935] transition-all placeholder-[#525252]";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#0D0D0D' }}>
      <div className="w-full max-w-[400px] animate-in fade-in zoom-in duration-500">

        {/* Logo Section */}
        <div className="flex flex-col items-center mb-8">
          <ShapeXLogo width={280} className="mb-2" />
          <p className="text-[#A1A1A1] text-xs sm:text-sm text-center tracking-wide">
            “A rotina forja o que a motivação nunca sustentou.”
          </p>
        </div>

        {/* Card Principal */}
        <div
          className="rounded-[24px] p-6 sm:p-8 border shadow-2xl backdrop-blur-sm"
          style={{
            backgroundColor: '#141414',
            borderColor: BORDER_COLOR,
            boxShadow: '0 0 40px rgba(0,0,0,0.5)'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Error Alert */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#E53935] shrink-0 mt-0.5" />
                <p className="text-[#E53935] text-xs font-medium leading-relaxed">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="E-mail"
                  className={inputClasses}
                  style={{
                    backgroundColor: INPUT_BG,
                    borderColor: error ? ACCENT_RED : BORDER_COLOR
                  }}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                />
              </div>

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Senha"
                  className={inputClasses}
                  style={{
                    backgroundColor: INPUT_BG,
                    borderColor: error ? ACCENT_RED : BORDER_COLOR
                  }}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1A1] hover:text-white transition-colors p-1"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                id="stayConnected"
                type="checkbox"
                className="w-4 h-4 rounded border-white/20 bg-[#0D0D0D] text-[#E53935] focus:ring-[#E53935] focus:ring-offset-0"
              />
              <label htmlFor="stayConnected" className="text-[#A1A1A1] text-xs cursor-pointer select-none">
                Manter conectado
              </label>
            </div>

            {/* Botão Primário - Padrão Oficial (Filled Red) */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: ACCENT_RED,
                color: 'white',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: '0 4px 20px rgba(229, 57, 53, 0.3)'
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#525252] text-xs">
              Ainda não tem uma conta?{' '}
              <button
                onClick={onSwitchToSignUp}
                className="text-[#E53935] font-bold hover:underline hover:text-red-400 transition-colors"
              >
                Cadastre-se
              </button>
            </p>
          </div>
        </div>

        {/* Botão PWA - Minimalista no canto */}
        <div className="mt-6 flex justify-end">
          <PWAInstall variant="login" />
        </div>
      </div>
    </div>
  );
}
